import type { FrequencyPeriod } from 'api/recoveryAi.types'

// AI-327 (FR-41): the auto-resolution period is stored/sent as an INTEGER
// day-count (7/14/30/60/90), but the FE display union (FrequencyPeriod) is '30d'.
// These two explicit 5-entry bijective maps convert at the pane boundary (no
// regex). An unknown day-count (backend drift) resolves to `undefined`, which the
// pane coalesces to its display default — never a crash.
const PERIOD_TO_DAYS: Record<FrequencyPeriod, number> = {
  '7d': 7,
  '14d': 14,
  '30d': 30,
  '60d': 60,
  '90d': 90,
}

// Derived from PERIOD_TO_DAYS (not hand-listed) so TS exhaustiveness-checking
// on that Record is the single source of truth for the period vocabulary.
export const FREQUENCY_PERIODS = Object.keys(
  PERIOD_TO_DAYS
) as FrequencyPeriod[]

// AI-1126: default for the frequent-complaint window. Held at 30 days so the UI
// reports the rule the backend actually enforces — Recovery::EvaluateAbuse
// hardcodes COMPLAINT_WINDOW_DAYS = 30 and both threshold gates read it. The
// blueprint's 90-day window is deferred to AI-438; until then a 90d default
// would misreport a rule that fires at 3-in-30. The nexus entity does not expose
// frequent_complaint_period (an unwired legacy column stored as a '{n}_days'
// VARCHAR — nucleus/db/schema.rb), so the wizard/settings/modal fall back to this
// display default. AI-438 is NOT a one-line flip of this constant: it must wire
// the column (expose + permit + reader) AND reconcile the '{n}_days' storage
// vocabulary with this '{n}d' display union — either a bijection alongside
// periodToDays/daysToPeriod, or normalize to a FrequencyPeriod token server-side.
export const DEFAULT_FREQUENT_PERIOD: FrequencyPeriod = '30d'

// Coerce a raw frequent-period value (from the config GET) to a valid display
// token. Returns DEFAULT_FREQUENT_PERIOD for undefined OR any value outside the
// FrequencyPeriod union — e.g. the backend's legacy '30_days' vocabulary, once
// AI-438 exposes the column. Mirrors how daysToPeriod absorbs an out-of-enum
// gaming day-count, so a non-token value can't reach the <select> and render it
// blank. The read-side counterpart to the write-side sentinel in EscalationRulesPane.
export const coerceFrequentPeriod = (
  raw: FrequencyPeriod | string | undefined
): FrequencyPeriod =>
  FREQUENCY_PERIODS.find(period => period === raw) ?? DEFAULT_FREQUENT_PERIOD

const DAYS_TO_PERIOD: Record<number, FrequencyPeriod> = {
  7: '7d',
  14: '14d',
  30: '30d',
  60: '60d',
  90: '90d',
}

// Translate a display period ('30d') to its integer day-count (30) for the PUT
// body + the isDirty comparison.
export const periodToDays = (period: FrequencyPeriod): number =>
  PERIOD_TO_DAYS[period]

// Translate an integer day-count (30) to the display period ('30d'). Returns
// `undefined` for an absent (or unknown) value so hydration can fall back to the
// display default (`?? '30d'`).
export const daysToPeriod = (
  days: number | undefined
): FrequencyPeriod | undefined =>
  days === undefined ? undefined : DAYS_TO_PERIOD[days]
